import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Bell, ChevronDown, Menu, Building2, Loader2 } from "lucide-react";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import EmpresaAvatar from "@/components/common/EmpresaAvatar";
import { useApp } from "@/context/AppContext";
import { useSprints360 } from "@/context/Sprints360Context";
import { useS360Consult } from "@/context/S360ConsultContext";
import { filtrarProjetosVisiveis } from "@/hooks/useProjetosVisiveis";
import { PROJETOS, NOTIFICACOES } from "@/data/mockData";
import { getNotifs, markAllRead, relativeLabel } from "@/lib/notificacoesStore";
import { scopeWorkspaceId } from "@/lib/impersonation";
import { PROJETOS_MOCK as S360_PROJETOS_BACKEND, useS360MgcItens } from "@/lib/s360MgcStore";

export default function Header({ onMenuClick }) {
  const { t } = useTranslation();
  const { papelAtual, empresaAtual, projetoAtual, setProjetoAtual, currentUser: sessionUser, clearSession, moduloAtual } = useApp();
  const { state: s360State, hydrated, pendingSyncCount, flushPendingSync } = useSprints360();
  const navigate = useNavigate();

  // Jun/2026 — "Salvar antes de sair". O logout apagava a sessão na hora,
  // deixando a fila de sincronização do Sprints360 para trás.
  const [sairAberto, setSairAberto] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [falhaRestantes, setFalhaRestantes] = useState(0);

  const encerrarSessao = useCallback(() => {
    setSairAberto(false);
    setGravando(false);
    setFalhaRestantes(0);
    clearSession();
    navigate("/");
  }, [clearSession, navigate]);

  const handleSairClick = useCallback(() => {
    if (pendingSyncCount > 0) {
      setFalhaRestantes(0);
      setSairAberto(true);
      return;
    }
    encerrarSessao();
  }, [pendingSyncCount, encerrarSessao]);

  const gravarESair = useCallback(async () => {
    setGravando(true);
    const r = await flushPendingSync();
    setGravando(false);
    if (r?.ok) encerrarSessao();
    else setFalhaRestantes(r?.restantes || 0);
  }, [flushPendingSync, encerrarSessao]);

  const sairDeQualquerForma = useCallback(() => encerrarSessao(), [encerrarSessao]);

  const { hasConsultoria, empresas: empresasConsult, empresaAtiva, setEmpresaAtivaId, hydrated: consultHydrated } = useS360Consult();
  const { pathname } = useLocation();

  // Phase 11.12 — Notificações dinâmicas (banner Val dispensado etc.) mescladas
  // com as notificações mock. O sino mostra as duas fontes juntas.
  const [notifBump, setNotifBump] = useState(0);
  useEffect(() => {
    const onUpd = () => setNotifBump(x => x + 1);
    window.addEventListener("notifs:updated", onUpd);
    return () => window.removeEventListener("notifs:updated", onUpd);
  }, []);
  const notifsDinamicas = useMemo(() => getNotifs(), [notifBump]);
  const totalNaoLidas = useMemo(
    () => notifsDinamicas.filter(n => !n.lida).length + NOTIFICACOES.filter(n => !n.lida).length,
    [notifsDinamicas]
  );

  // Detecção alinhada com a Sidebar (Phase 7.40b) — considera moduloAtual em rotas neutras
  const inV360Route    = pathname.startsWith("/app/v360");
  const inSprintsRoute = ["/app/dashboard","/app/projetos","/app/recomendacoes","/app/sprint-board","/app/gantt","/app/jornada","/app/calendario","/app/documentos","/app/reunioes","/app/painel-semanal","/app/sprints360"].some(p => pathname.startsWith(p));
  const isV360 = inV360Route || (!inSprintsRoute && moduloAtual === "valora360");
  const appName = isV360 ? "Luoggo Gestão" : "Luoggo Projetos";
  const appAccent = isV360 ? "violet" : "blue";
  // Projetos disponíveis = Sprints360 (state.projetos) + Legacy (PROJETOS mock),
  // ambos filtrados pela empresa ativa. Fonte-única alinhada com a página Projetos.
  //
  // Fev/2026 — Agora usa o MESMO `filtrarProjetosVisiveis` que `Projetos.jsx`
  // (hook `useProjetosVisiveis`), em vez de um filtro inline duplicado. Os
  // dois tinham regras ligeiramente diferentes no modo NÃO-consultoria
  // (Header exigia `empresa_id`/`empresa_nome` batendo; a página Projetos,
  // via hook, mostra todo o tenant) — essa divergência fazia o Header
  // considerar `projetoAtual` "inválido" para um projeto que a página
  // Projetos já exibia como válido, disparando a troca indevida de projeto.
  // Phase 11.0 — Plano CONSULTORIA:
  //   • Quando ativo, projetos S360 são filtrados por `empresa_simulada_id` da
  //     empresa ativa da consultoria (não pela empresa V360 mockada).
  //   • Projetos legados (PROJETOS mock) NÃO aparecem no modo Consultoria —
  //     pertencem à empresa V360 antiga (isolamento total).
  // Phase 12.6 — Projetos S360 vindos do BACKEND (via s360MgcStore.bootstrap).
  // Antes, o Header consumia só `s360State.projetos` do Sprints360Context, que
  // fica VAZIO em tenants reais (Phase 12.4 removeu o seed). Como o bootstrap
  // do MgcStore já popula `PROJETOS_MOCK` com os projetos reais do backend
  // (com codigo/nome corretos), usamos essa fonte também.
  useS360MgcItens();          // dispara o bootstrap online (efeito colateral)
  const [projetosBootstrap, setProjetosBootstrap] = useState(() => [...S360_PROJETOS_BACKEND]);
  useEffect(() => {
    const upd = () => setProjetosBootstrap([...S360_PROJETOS_BACKEND]);
    window.addEventListener("s360-mgc:updated", upd);
    window.addEventListener("app-session-changed", upd);
    upd();
    return () => {
      window.removeEventListener("s360-mgc:updated", upd);
      window.removeEventListener("app-session-changed", upd);
    };
  }, []);

  // União das duas fontes (dedup por id) — mantém compat com o
  // Sprints360Context que ainda é usado pela aba Projeto.
  // Phase 12.28r — projetosBootstrap agora também respeita o filtro de
  // empresa simulada em modo CONSULTORIA. Antes, projetos órfãos
  // (empresa_simulada_id=NULL) do bootstrap "vazavam" para o seletor,
  // criando o estado inconsistente "empresa Chicken-fil-A › projeto
  // MARTE2026" quando MARTE2026 não pertencia a Chicken-fil-A.
  const projetosContext = filtrarProjetosVisiveis(s360State.projetos, {
    empresaAtual, hasConsultoria, empresaConsultAtiva: empresaAtiva,
  });
  const idsContext = new Set(projetosContext.map(p => p.id));
  const projetosBootstrapFiltrados = projetosBootstrap.filter(p => {
    if (idsContext.has(p.id)) return false;
    if (hasConsultoria) {
      // Em CONSULTORIA só entra o que pertence explicitamente à empresa
      // cliente ativa. Projetos órfãos (empresa_simulada_id ausente) não
      // aparecem no seletor da consultoria.
      return empresaAtiva && p.empresa_simulada_id === empresaAtiva.id;
    }
    return true;
  });
  const s360Projetos = [
    ...projetosContext.map(p => ({
      id: p.id,
      nome: p.nome,
      codigo: p.codigo || `S360-${String(p.id).slice(-4).toUpperCase()}`,
      status: p.status || "Planejamento",
      empresaId: hasConsultoria ? empresaAtiva?.id : empresaAtual.id,
      _isS360: true,
    })),
    ...projetosBootstrapFiltrados.map(p => ({
      id: p.id,
      nome: p.nome,
      codigo: p.codigo || `S360-${String(p.id).slice(-4).toUpperCase()}`,
      status: "Planejamento",
      empresaId: hasConsultoria ? empresaAtiva?.id : empresaAtual.id,
      _isS360: true,
    })),
  ];
  // Phase 12.4 — Legados PROJETOS mock só aparecem em workspaces DEMO
  // (não-__live). Para clientes reais, o Header inicia sem projeto sugerido.
  const isDemoMock = !empresaAtual?.__live;
  const legacyProjetos = (hasConsultoria || !isDemoMock)
    ? []
    : PROJETOS
      .filter(p => p.empresaId === empresaAtual.id)
      .map(p => ({ ...p, _isS360: false }));
  const projetosDaEmpresa = [...s360Projetos, ...legacyProjetos];
  // Se o projeto atual não está mais na lista da empresa, cai no primeiro disponível.
  const projetoAtualValido = projetosDaEmpresa.find(p => p.id === projetoAtual?.id);
  // Jun/2026 — Lembra o projeto ativo por workspace. Sem isso, cada recarga /
  // troca de módulo voltava para o primeiro da lista (e, se a hidratação
  // atrasava, para "Selecionar").
  const lsProjetoAtivoKey = (() => {
    const ws = scopeWorkspaceId();
    return ws ? `s360_projeto_ativo__${ws}` : null;
  })();
  const escolherProjetoAtivo = (lista) => {
    let preferido = null;
    try {
      const raw = lsProjetoAtivoKey ? localStorage.getItem(lsProjetoAtivoKey) : null;
      if (raw) {
        let savedId = raw;
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.id) savedId = parsed.id;
        } catch { /* formato legado: raw já é o id puro */ }
        preferido = lista.find(p => p.id === savedId);
      }
    } catch { /* noop */ }
    return preferido;
  };
  // Escolha explícita do usuário — a persistência em localStorage já é feita
  // centralmente pelo TenantContext (Item B), sem duplicar aqui.
  // Fev/2026 — Esta função agora é a ÚNICA responsável por navegar para a
  // tela de detalhe quando o projeto é trocado pelo seletor do Header
  // (antes disso era um `useEffect` passivo em Sprints360Projeto.jsx que
  // reagia a QUALQUER mudança de `projetoAtual` — inclusive mudanças vindas
  // de outras telas/efeitos — e acabava trocando a URL para um projeto que
  // o usuário não escolheu. Ver "Projetos abre o projeto errado ao clicar
  // no card").
  const selecionarProjeto = (p) => {
    setProjetoAtual(p);
    if (p?.id && pathname.startsWith("/app/sprints360/")) {
      navigate(`/app/sprints360/${p.id}`);
    }
  };
  // Fev/2026 — Só cai no projeto default (preferido do localStorage ou o
  // primeiro da lista) depois que a lista do Sprints360 já hidratou
  // (`hydrated`). Antes, esta checagem rodava a cada render — mesmo com a
  // lista ainda carregando — e podia concluir "projetoAtual inválido" por
  // uma janela de tempo em que `projetosDaEmpresa` simplesmente ainda não
  // refletia o projeto recém-selecionado em outra tela (ex.: clique num
  // card em Projetos.jsx), sobrescrevendo-o por um projeto arbitrário.
  useEffect(() => {
    if (!hydrated) return;
    if (projetoAtualValido || projetosDaEmpresa.length === 0) return;
    const preferido = escolherProjetoAtivo(projetosDaEmpresa);
    setProjetoAtual(preferido || projetosDaEmpresa[0]);
  }, [projetoAtualValido, projetosDaEmpresa, setProjetoAtual, hydrated]);
  // Fev/2026 — NUNCA mais cair no usuário mock ("Camila Andrade") enquanto a
  // sessão real ainda não carregou. Antes, `sessionUser` null (gap entre o
  // navigate() do Login e o `sessionBridge` terminar de gravar `app_session`)
  // fazia o Header mostrar um nome de pessoa diferente do usuário logado —
  // reportado como bug sério. Agora, sem sessão resolvida, `currentUser` é
  // `null` e o bloco de perfil (abaixo) renderiza um placeholder de loading
  // em vez de qualquer nome/avatar.
  const currentUser = sessionUser || null;

  return (
    <header className="h-14 sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200" data-testid="app-header">
      <div className="h-full px-3 sm:px-5 flex items-center gap-2 sm:gap-4">

        {/* Hambúrguer mobile */}
        <Button
          variant="ghost" size="icon"
          className="h-9 w-9 lg:hidden text-slate-600 hover:bg-slate-100 shrink-0 -ml-1"
          onClick={onMenuClick}
          data-testid="mobile-menu-trigger"
          aria-label={t("header.abrir_menu")}
        >
          <Menu className="w-5 h-5" />
        </Button>

        {/* 1 + 2. Nome do App + Nome da Empresa (Título) */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <div className="hidden sm:flex items-center gap-2 shrink-0" data-testid="header-app-name">
            {/* Jun/2026 — wordmark Luoggo por módulo (substitui o selo V/S + texto) */}
            <img
              src={isV360 ? "/luoggo-gestao.png" : "/luoggo-projetos.png"}
              alt={appName}
              title={appName}
              className="h-[27px] w-auto"
              data-testid="header-app-logo"
            />
          </div>

          <div className="w-px h-5 bg-slate-200 hidden sm:block" />

          {/* Empresa contratante / Empresa cliente
              Phase 12.28 — Em CONSULTORIA, mostra as DUAS empresas lado a lado:
              (a) empresa REAL do workspace S360 (contratante/consultor) e
              (b) empresa SIMULADA ativa (cliente sendo atendido).
              Fora do CONSULTORIA, mostra somente a empresa contratante. */}
          {!isV360 && hasConsultoria ? (
            <>
              {/* (a) Empresa REAL do workspace S360 (contratante/consultor) */}
              <div
                className="w-8 h-8 rounded-md grid place-items-center text-white shrink-0 bg-blue-700"
                data-testid="header-empresa-real-avatar"
                title={empresaAtual.nome}
              >
                <Building2 className="w-4 h-4" />
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-[9px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                  Empresa contratante
                </span>
                <h1
                  className="font-display text-base sm:text-lg font-semibold text-slate-900 truncate tracking-tight"
                  data-testid="header-empresa-real-title"
                >
                  {empresaAtual.nome}
                </h1>
              </div>

              {/* Separador visual entre S360 real e Empresa cliente */}
              <div className="w-px h-6 bg-slate-200 hidden md:block shrink-0" aria-hidden />

              {/* (b) Empresa SIMULADA ativa (cliente da consultoria) */}
              {empresaAtiva ? (
                <>
                  <EmpresaAvatar empresa={empresaAtiva} size="md" className="shrink-0" />
                  <div className="flex flex-col leading-tight min-w-0">
                    <span className="text-[9px] uppercase tracking-[0.14em] text-slate-400 font-semibold">
                      Empresa cliente
                    </span>
                    <h1
                      className="font-display text-base sm:text-lg font-semibold text-slate-900 truncate tracking-tight"
                      data-testid="header-empresa-cliente-title"
                    >
                      {empresaAtiva.nome}
                    </h1>
                  </div>
                </>
              ) : (
                <span className="text-[11px] text-slate-400 italic hidden md:inline">
                  (nenhuma empresa cliente ativa)
                </span>
              )}
            </>
          ) : (
            <>
              <div
                className={`w-8 h-8 rounded-md grid place-items-center text-white shrink-0 ${isV360 ? "bg-violet-700" : "bg-blue-700"}`}
                data-testid="header-empresa-contratante-avatar"
                title={empresaAtual.nome}
              >
                <Building2 className="w-4 h-4" />
              </div>
              <div className="flex flex-col leading-tight min-w-0">
                <h1 className="font-display text-base sm:text-xl font-semibold text-slate-900 truncate tracking-tight" data-testid="header-empresa-title">
                  {empresaAtual.nome}
                </h1>
              </div>
            </>
          )}

          {/* Seletor Hierárquico "Empresa › Projeto" — apenas Luoggo Projetos + Consultoria */}
          {!isV360 && hasConsultoria && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="header-consult-selector"
                        className="h-8 border-slate-200 text-slate-700 font-medium gap-1.5 hover:bg-slate-50 shrink-0 pl-1.5">
                  {empresaAtiva && <EmpresaAvatar empresa={empresaAtiva} size="sm" />}
                  <div className="flex flex-col items-start leading-tight">
                    <span className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold">Empresa › Projeto</span>
                    <span className="text-sm truncate max-w-[260px]">
                      {empresaAtiva ? (
                        <>
                          {empresaAtiva.nome}
                          {projetoAtualValido ? (
                            <span className="text-slate-400"> › {projetoAtualValido.nome}</span>
                          ) : (
                            <span className="text-slate-400 italic"> › (sem projetos ainda)</span>
                          )}
                        </>
                      ) : (
                        <span className="text-slate-400 italic">
                          {consultHydrated ? "Selecionar empresa e projeto" : "Carregando…"}
                        </span>
                      )}
                    </span>
                  </div>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-96 max-h-[70vh] overflow-y-auto">
                <DropdownMenuLabel>Selecione uma empresa e projeto</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {empresasConsult.length === 0 && (
                  <div className="px-3 py-3 text-xs text-slate-500">
                    Nenhuma empresa cadastrada.{" "}
                    <Link to="/app/s360-empresas" className="text-blue-700 hover:underline">Cadastrar agora</Link>
                  </div>
                )}
                {empresasConsult.map(emp => {
                  const projetosDaEmp = (s360State.projetos || [])
                    .filter(p => p.empresa_simulada_id === emp.id)
                    .map(p => ({
                      id: p.id, nome: p.nome, _isS360: true,
                      codigo: `S360-${String(p.id).slice(-4).toUpperCase()}`,
                    }));
                  const isEmpAtiva = emp.id === empresaAtiva?.id;
                  return (
                    <div key={emp.id} className={cn("py-1", isEmpAtiva && "bg-blue-50/40")}>
                      <button
                        type="button"
                        onClick={() => {
                          // Phase 12.28r — Ativa a empresa mesmo se ela não
                          // tiver projetos ainda: assim o usuário consegue
                          // ir para /app/projetos e clicar em "Novo projeto"
                          // já com a empresa cliente vinculada como ativa.
                          setEmpresaAtivaId(emp.id);
                          if (projetosDaEmp.length > 0) selecionarProjeto(projetosDaEmp[0]);
                          else setProjetoAtual(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100 text-left"
                        data-testid={`consult-empresa-option-${emp.id}`}
                      >
                        <EmpresaAvatar empresa={emp} size="sm" />
                        <span className="text-sm font-semibold text-slate-800 flex-1 truncate">{emp.nome}</span>
                        {isEmpAtiva && (
                          <span className="text-[9px] font-semibold text-blue-700 uppercase tracking-wider shrink-0">Ativa</span>
                        )}
                        <span className="text-[10px] text-slate-500 shrink-0">{projetosDaEmp.length} proj.</span>
                      </button>
                      {projetosDaEmp.length === 0 ? (
                        <div className="pl-10 pr-3 py-1 text-[11px] text-slate-400 italic">Nenhum projeto</div>
                      ) : (
                        projetosDaEmp.map(p => {
                          const isPAtivo = projetoAtualValido?.id === p.id && isEmpAtiva;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => { setEmpresaAtivaId(emp.id); selecionarProjeto(p); }}
                              className={cn(
                                "w-full flex items-center gap-2 pl-10 pr-3 py-1.5 text-left hover:bg-slate-50",
                                isPAtivo && "bg-blue-50"
                              )}
                              data-testid={`consult-projeto-option-${p.id}`}
                            >
                              <span className="text-slate-300 text-xs">›</span>
                              <span className="text-sm text-slate-700 truncate flex-1">{p.nome}</span>
                              {isPAtivo && (
                                <span className="text-[9px] font-semibold text-blue-700 uppercase tracking-wider shrink-0">Ativo</span>
                              )}
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-blue-50 border-blue-200 text-blue-700">S360</Badge>
                            </button>
                          );
                        })
                      )}
                    </div>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {/* Seletor de Projetos legado — Luoggo Projetos sem Consultoria */}
          {!isV360 && !hasConsultoria && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="header-projeto-selector"
                        className="h-8 border-slate-200 text-slate-700 font-medium gap-1.5 hover:bg-slate-50 flex-1 min-w-[150px] max-w-[560px] justify-start">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold shrink-0">Projeto</span>
                  <span className="text-sm truncate flex-1 text-left">{projetoAtualValido?.nome || projetoAtual?.nome || "Selecionar"}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[min(92vw,26rem)]">
                <DropdownMenuLabel>Projetos · {empresaAtual.nome}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {projetosDaEmpresa.map(p => (
                  <DropdownMenuItem key={p.id} onClick={() => selecionarProjeto(p)} data-testid={`projeto-option-${p.id}`}>
                    <div className="flex items-start gap-2 w-full">
                      <div className={`w-1 self-stretch rounded-full ${p._isS360 ? "bg-blue-600" : "bg-slate-300"}`} />
                      <div className="flex flex-col flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-medium truncate">{p.nome}</span>
                          {projetoAtualValido?.id === p.id && (
                            <span className="text-[9px] font-semibold text-blue-700 uppercase tracking-wider shrink-0">Ativo</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] uppercase tracking-wider text-slate-500">{p.codigo}</span>
                          <span className="text-[10px] text-slate-400">·</span>
                          <span className="text-[10px] text-slate-500">{p.status}</span>
                          {p._isS360 && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-4 bg-blue-50 border-blue-200 text-blue-700">S360</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
                {projetosDaEmpresa.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">Nenhum projeto nesta empresa.</div>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* 4. Notificações + Perfil (ponta direita) */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Popover onOpenChange={(o) => { if (o) markAllRead(); }}>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative h-9 w-9 text-slate-600 hover:bg-slate-100" data-testid="header-notifications">
                <Bell className="w-[18px] h-[18px]" />
                {totalNaoLidas > 0 && (
                  <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold grid place-items-center leading-none">
                    {totalNaoLidas > 9 ? "9+" : totalNaoLidas}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="font-semibold text-sm text-slate-900">{t("header.notificacoes")}</div>
                {totalNaoLidas > 0 && (
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border border-blue-200">
                    {totalNaoLidas} nova{totalNaoLidas === 1 ? "" : "s"}
                  </Badge>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {/* Notificações dinâmicas primeiro (mais recentes) */}
                {notifsDinamicas.map(n => (
                  <div key={n.id} className={`px-4 py-3 hover:bg-slate-50 ${!n.lida ? "bg-amber-50/30" : ""}`} data-testid={`notif-dinamica-${n.id}`}>
                    <div className="flex gap-2.5 items-start">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${
                        n.tipo === "consumo" ? "bg-amber-500" :
                        n.tipo === "atraso"  ? "bg-red-500" :
                        n.tipo === "sucesso" ? "bg-green-500" :
                        n.tipo === "evento"  ? "bg-blue-500" : "bg-slate-400"
                      }`} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{n.titulo}</div>
                        <div className="text-xs text-slate-500 mt-0.5 leading-snug">{n.descricao}</div>
                        <div className="flex items-center justify-between mt-1">
                          <div className="text-[11px] text-slate-400">{n.tempo || relativeLabel(n.created_at)}</div>
                          {n.cta_href && (
                            <Link
                              to={n.cta_href}
                              className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 hover:underline"
                              data-testid={`notif-cta-${n.id}`}
                            >
                              {n.cta_label || "Abrir"} →
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {NOTIFICACOES.map(n => (
                  <div key={n.id} className={`px-4 py-3 hover:bg-slate-50 ${!n.lida ? "bg-blue-50/30" : ""}`} data-testid={`notif-${n.id}`}>
                    <div className="flex gap-2.5 items-start">
                      <div className={`w-1.5 h-1.5 rounded-full mt-1.5 ${
                        n.tipo === "atraso" ? "bg-red-500" :
                        n.tipo === "sucesso" ? "bg-green-500" :
                        n.tipo === "evento" ? "bg-blue-500" : "bg-slate-400"
                      }`} />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-slate-900">{n.titulo}</div>
                        <div className="text-xs text-slate-500 mt-0.5">{n.descricao}</div>
                        <div className="text-[11px] text-slate-400 mt-1">{n.tempo}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2 border-t border-slate-200 text-center">
                <Button variant="ghost" size="sm" className="text-blue-700 hover:bg-blue-50 text-xs">{t("header.ver_todas")}</Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Jun/2026 — Sincronização pendente do Luoggo Projetos */}
          {pendingSyncCount > 0 && (
            <div
              className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-200 text-[11px] font-medium text-amber-800 shrink-0"
              data-testid="header-sync-indicator"
              title="Alterações do Luoggo Projetos ainda sendo gravadas no servidor"
            >
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="whitespace-nowrap hidden sm:inline">Sincronizando {pendingSyncCount}…</span>
              <span className="sm:hidden" aria-hidden="true">{pendingSyncCount}</span>
            </div>
          )}

          {/* Usuário ativo — Fev/2026: enquanto `currentUser` não resolveu
              (sessão real ainda carregando), mostra placeholder neutro.
              Nunca renderiza nome/iniciais de outro usuário nesse meio-tempo. */}
          {currentUser ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-2 pr-1.5 py-1 rounded-md hover:bg-slate-50 shrink-0" data-testid="header-profile">
                  <div className="text-right hidden xl:block leading-tight">
                    <div className="text-[13px] font-medium text-slate-900 whitespace-nowrap">{(currentUser.nome || "").split(" ")[0]}</div>
                    <div className="text-[11px] text-slate-500 whitespace-nowrap">{papelAtual}</div>
                  </div>
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={currentUser.avatar} alt={currentUser.nome} />
                    <AvatarFallback className="text-xs bg-blue-100 text-blue-800">{currentUser.iniciais}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem>{t("header.preferencias")}</DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="header-logout"
                  // Sem `preventDefault`: o DropdownMenu precisa fechar antes de
                  // abrir o AlertDialog, senão o Radix empilha dois locks de
                  // pointer-events e o body fica travado ao fechar o diálogo.
                  onSelect={() => setTimeout(handleSairClick, 0)}
                >
                  {t("header.sair")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2 pl-2 pr-1.5 py-1 rounded-md shrink-0" data-testid="header-profile-loading">
              <div className="text-right hidden xl:block leading-tight">
                <div className="h-3 w-14 rounded bg-slate-200 animate-pulse mb-1" />
                <div className="h-2.5 w-10 rounded bg-slate-100 animate-pulse" />
              </div>
              <Avatar className="w-8 h-8">
                <AvatarFallback className="text-xs bg-slate-100 text-slate-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                </AvatarFallback>
              </Avatar>
            </div>
          )}
        </div>
      </div>

      {/* Jun/2026 — Salvar antes de sair: nunca descartar a fila em silêncio */}
      <AlertDialog open={sairAberto} onOpenChange={(o) => { if (!gravando) setSairAberto(o); }}>
        <AlertDialogContent data-testid="logout-sync-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {falhaRestantes > 0 ? "Não foi possível gravar tudo" : "Alterações ainda não gravadas"}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="logout-sync-message">
              {falhaRestantes > 0
                ? `Ainda restam ${falhaRestantes} operação(ões) pendente(s). Elas ficam salvas neste navegador e serão enviadas automaticamente no próximo login.`
                : `Há ${pendingSyncCount} alteração(ões) do Luoggo Projetos que ainda não foram gravadas no servidor. Podemos terminar de gravar antes de sair.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={gravando} data-testid="logout-continuar-editando">
              Continuar editando
            </AlertDialogCancel>
            {falhaRestantes > 0 ? (
              <Button variant="destructive" onClick={sairDeQualquerForma} data-testid="logout-sair-mesmo-assim">
                Sair mesmo assim
              </Button>
            ) : (
              <Button onClick={gravarESair} disabled={gravando} data-testid="logout-gravar-e-sair">
                {gravando ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gravando…</>) : "Gravar e sair"}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
